import * as THREE from "three";

export type SpawnableKind =
  | "car"       // slower traffic car — dodge sideways
  | "coin"
  | "magnet"
  | "shield"
  | "nitro"
  | "x2";

export interface Spawnable {
  kind: SpawnableKind;
  mesh: THREE.Object3D;
  active: boolean;
  halfExtents: THREE.Vector3;
}

// Shared cheap materials
const coinMat = new THREE.MeshStandardMaterial({
  color: 0xffcd3c, emissive: 0xffa500, emissiveIntensity: 0.7,
  roughness: 0.3, metalness: 0.9,
});
const magnetMat = new THREE.MeshStandardMaterial({
  color: 0xff4d6d, emissive: 0xff2a55, emissiveIntensity: 0.6,
});
const shieldMat = new THREE.MeshStandardMaterial({
  color: 0x4dd0e1, emissive: 0x18ffff, emissiveIntensity: 0.6,
  transparent: true, opacity: 0.7,
});
const nitroMat = new THREE.MeshStandardMaterial({
  color: 0xb6ff3c, emissive: 0x76ff03, emissiveIntensity: 0.6,
});
const x2Mat = new THREE.MeshStandardMaterial({
  color: 0xc77dff, emissive: 0x9d4edd, emissiveIntensity: 0.6,
});

// Traffic car body color palette
const CAR_COLORS = [
  0xff4444, 0x4488ff, 0x44cc66, 0xffcc33,
  0xbbbbbb, 0xdd55cc, 0x33aadd, 0xff8844,
  0xffffff, 0x6655ff, 0xff5533, 0x55aaff,
];

export function createSpawnable(kind: SpawnableKind): Spawnable {
  let mesh: THREE.Mesh | THREE.Group;
  const half = new THREE.Vector3();

  switch (kind) {
    case "car": {
      const color = CAR_COLORS[Math.floor(Math.random() * CAR_COLORS.length)];
      const bodyMat = new THREE.MeshStandardMaterial({
        color, roughness: 0.35, metalness: 0.55,
        emissive: color, emissiveIntensity: 0.12,
      });
      const cabinMat = new THREE.MeshStandardMaterial({
        color: 0x111219, roughness: 0.25, metalness: 0.3,
      });
      const tailMat = new THREE.MeshStandardMaterial({
        color: 0xff3344, emissive: 0xff2233, emissiveIntensity: 1.2,
      });
      const wheelMat = new THREE.MeshStandardMaterial({
        color: 0x111111, roughness: 0.9,
      });

      const g = new THREE.Group();

      const body = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.45, 2.4), bodyMat);
      body.position.y = 0.35;

      const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.38, 1.1), cabinMat);
      cabin.position.y = 0.71;
      cabin.position.z = -0.05;

      // Tail lights glow red (player sees car from behind)
      const tail1 = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.09, 0.04), tailMat);
      tail1.position.set(0.38, 0.44, 1.22);
      const tail2 = tail1.clone();
      tail2.position.x = -0.38;

      // Wheels
      const wheelGeo = new THREE.CylinderGeometry(0.22, 0.22, 0.2, 10);
      wheelGeo.rotateZ(Math.PI / 2);
      for (const [wx, wz] of [[0.6, 0.85], [-0.6, 0.85], [0.6, -0.85], [-0.6, -0.85]] as [number, number][]) {
        const w = new THREE.Mesh(wheelGeo, wheelMat);
        w.position.set(wx, 0.22, wz);
        g.add(w);
      }

      g.add(body, cabin, tail1, tail2);
      mesh = g;
      half.set(0.65, 0.55, 1.2);
      break;
    }
    case "coin": {
      const torus = new THREE.Mesh(new THREE.TorusGeometry(0.28, 0.07, 12, 24), coinMat);
      torus.rotation.y = Math.PI / 2;
      torus.position.y = 1.0;
      mesh = torus;
      half.set(0.32, 0.32, 0.1);
      break;
    }
    case "magnet": {
      mesh = new THREE.Mesh(new THREE.IcosahedronGeometry(0.4, 0), magnetMat);
      (mesh as THREE.Mesh).position.y = 1.2;
      half.set(0.45, 0.45, 0.45);
      break;
    }
    case "shield": {
      mesh = new THREE.Mesh(new THREE.SphereGeometry(0.4, 12, 10), shieldMat);
      (mesh as THREE.Mesh).position.y = 1.2;
      half.set(0.45, 0.45, 0.45);
      break;
    }
    case "nitro": {
      mesh = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.7, 12), nitroMat);
      (mesh as THREE.Mesh).rotation.x = Math.PI;
      (mesh as THREE.Mesh).position.y = 1.2;
      half.set(0.4, 0.5, 0.4);
      break;
    }
    case "x2": {
      mesh = new THREE.Mesh(new THREE.OctahedronGeometry(0.4, 0), x2Mat);
      (mesh as THREE.Mesh).position.y = 1.2;
      half.set(0.45, 0.45, 0.45);
      break;
    }
  }

  mesh.visible = false;
  return { kind, mesh, active: false, halfExtents: half };
}
